import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Icon, I } from '../../components/ui'
import { useIsMobileViewport } from '../../hooks/useIsMobileViewport'
import { PublicPageLayout, type PublicDestination } from './PublicPageLayout'

const CATEGORIES = ['all', 'account', 'integrations', 'sync', 'data', 'billing'] as const
type Category = typeof CATEGORIES[number]

const QUESTIONS: ReadonlyArray<{ key: string; category: Exclude<Category, 'all'> }> = [
  { key: 'connect', category: 'integrations' },
  { key: 'providers', category: 'integrations' },
  { key: 'synchronize', category: 'sync' },
  { key: 'failure', category: 'sync' },
  { key: 'stock', category: 'sync' },
  { key: 'security', category: 'data' },
  { key: 'rights', category: 'data' },
  { key: 'amazon', category: 'data' },
  { key: 'team', category: 'account' },
  { key: 'plans', category: 'billing' },
]

export function FaqPage({ onNavigate }: { onNavigate: (destination: PublicDestination) => void }) {
  const { t } = useTranslation()
  const isMobile = useIsMobileViewport()
  const [category, setCategory] = useState<Category>('all')
  const [openQuestions, setOpenQuestions] = useState<Set<string>>(() => new Set())
  const questions = category === 'all' ? QUESTIONS : QUESTIONS.filter(question => question.category === category)

  function toggleQuestion(key: string) {
    setOpenQuestions(current => {
      const next = new Set(current)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  return (
    <PublicPageLayout active="faq" onNavigate={onNavigate}>
      <header style={{ borderBottom: '1px solid var(--border-subtle)', padding: isMobile ? 'var(--sp-8) var(--sp-6)' : 'var(--sp-12) min(8.33vw, 120px) var(--sp-10)' }}>
        <h1 style={{ color: 'var(--brand-900)', fontSize: isMobile ? 'var(--fs-30)' : 'var(--fs-48)', fontWeight: 'var(--fw-extra)', lineHeight: 'var(--lh-snug)', margin: 0 }}>{t('public.site.faq.title')}</h1>
        <p style={{ color: 'var(--fg-muted)', fontSize: isMobile ? 'var(--fs-16)' : 'var(--fs-18)', lineHeight: 'var(--lh-normal)', margin: 'var(--sp-2) 0 0' }}>{t('public.site.faq.subtitle')}</p>
      </header>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-6)', margin: '0 auto', maxWidth: 880, padding: isMobile ? 'var(--sp-8) var(--sp-6) var(--sp-16)' : 'var(--sp-10) var(--sp-6) var(--sp-20)' }}>
        <div>
          <strong style={{ color: 'var(--fg-muted)', display: 'block', fontSize: 'var(--fs-14)', marginBottom: 'var(--sp-4)' }}>{t('public.site.faq.browse')}</strong>
          <div role="group" aria-label={t('public.site.faq.categoriesAria')} style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-2)' }}>
            {CATEGORIES.map(item => (
              <button key={item} type="button" aria-pressed={category === item} onClick={() => setCategory(item)} style={{ background: category === item ? 'var(--brand-500)' : 'var(--ink-100)', border: 0, borderRadius: 'var(--r-pill)', color: category === item ? 'var(--white)' : 'var(--fg-muted)', cursor: 'pointer', font: 'inherit', fontSize: 'var(--fs-13)', padding: 'var(--sp-2) var(--sp-3)' }}>
                {t(`public.site.faq.categories.${item}`)}
              </button>
            ))}
          </div>
        </div>
        <div style={{ border: '1px solid var(--border-subtle)', borderRadius: 'var(--r-md)', overflow: 'hidden' }}>
          {questions.map((question, index) => {
            const open = openQuestions.has(question.key)
            const answerId = `faq-answer-${question.key}`
            return (
              <div key={question.key} style={{ background: 'var(--white)', borderBottom: index === questions.length - 1 ? undefined : '1px solid var(--border-subtle)' }}>
                <button type="button" aria-expanded={open} aria-controls={answerId} onClick={() => toggleQuestion(question.key)} style={{ alignItems: 'center', background: 'transparent', border: 0, color: 'var(--brand-900)', cursor: 'pointer', display: 'flex', font: 'inherit', fontSize: 'var(--fs-16)', justifyContent: 'space-between', padding: 'var(--sp-4)', textAlign: 'start', width: '100%' }}>
                  {t(`public.site.faq.questions.${question.key}.question`)}
                  <Icon size={18} color="var(--brand-500)" style={{ transform: open ? 'rotate(180deg)' : undefined, transition: 'transform var(--dur-fast) var(--ease-out)' }}>{I.chevronD}</Icon>
                </button>
                <p id={answerId} hidden={!open} style={{ background: 'var(--ink-50)', color: 'var(--fg-muted)', fontSize: 'var(--fs-14)', lineHeight: 'var(--lh-relaxed)', margin: 0, padding: 'var(--sp-4)' }}>{t(`public.site.faq.questions.${question.key}.answer`)}</p>
              </div>
            )
          })}
        </div>
      </div>
    </PublicPageLayout>
  )
}
